import { useContext, useEffect, useState } from "react";
import {
	Box,
	Button,
	Divider,
	IconButton,
	Modal,
	Typography,
} from "@mui/material";
import {
	Chart as ChartJS,
	CategoryScale,
	LinearScale,
	BarElement,
	Title,
	Tooltip,
	Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import CityContext from "../../contexts/CityContext";
import * as geoprocessor from "@arcgis/core/rest/geoprocessor";
import CloseIcon from "@mui/icons-material/Close";
import React from "react";
import JobInfo from "@arcgis/core/rest/support/JobInfo";
import {
	gpPDF_URL,
	parkLabels,
	dataFields,
} from "../../constants";
import ParkContext from "../../contexts/ParkContext";
import { parkReport } from "../../reports/parkReport";
import FeatureSet from "@arcgis/core/rest/support/FeatureSet";
import {
	ageGroupInServiceArea,
	ageLabels,
	colors,
	demographicsInServiceArea,
	demoLabels,
	incomeGroupInServiceArea,
	incomeLabels,
	options,
} from "./ClickedParkResultChartInfo";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { trackEvent } from "../../hooks/useAnalytics";

const modalStyle = {
	position: "absolute" as "absolute",
	top: "50%",
	left: "50%",
	transform: "translate(-50%, -50%)",
	width: 400,
	bgcolor: "background.paper",
	boxShadow: 24,
	p: 4,
	display: "flex",
	flexDirection: "column",
	alignItems: "center",
};


export default function ClickedParkPanel() {
	const { clickedPark, clickedParkServiceArea } = useContext(ParkContext);
	const [parkData, setParkData] = useState(clickedPark);

	const { city, urban } = useContext(CityContext);

	// The `open` state and the two following functions are used to control whether or not the modal is displayed
	const [open, setOpen] = React.useState(false);
	const handleOpen = () => setOpen(true);
	const handleClose = () => setOpen(false);

	// These three state variables store data related to the GP Service request.
	// jobMessage is the current message returned from the GP Service while it is in progress and displayed to the user
	// pdfUrl is returned when the service is complete and is the location of the resulting report
	// gpSuccess handles whether the report request was successful or not
	const [jobMessage, setJobMessage] = React.useState<string>("");
	const [pdfUrl, setPdfUrl] = React.useState<string>("");
	const [gpSuccess, setGPSuccess] = React.useState(false);

	// The incomeData, ageData, and demoData state variables are used to store the respective datasets used to generate the charts
	const [incomeData, setIncomeData] = useState<any>({
		labels: incomeLabels,
		datasets: [
			{
				label: "income",
				data: [0, 0, 0],
				borderColor: colors,
				backgroundColor: colors,
			},
		],
	});
	const [ageData, setAgeData] = useState<any>({
		labels: ageLabels,
		datasets: [
			{
				label: "age",
				data: [0, 0, 0],
				borderColor: colors,
				backgroundColor: colors,
			},
		],
	});
	const [demoData, setDemoData] = useState<any>({
		labels: demoLabels,
		datasets: [
			{
				label: "demographics",
				data: [0, 0, 0, 0, 0, 0, 0, 0],
				borderColor: colors,
				backgroundColor: colors,
			},
		],
	});

	useEffect(() => {
		if (clickedPark) {
			setParkData(clickedPark);
			setIncomeData({
				labels: incomeLabels,
				datasets: [
					{
						label: "income",
						data: incomeGroupInServiceArea.map(
							(x: any) => clickedPark?.attributes[dataFields[x.value]]
						),
						borderColor: colors,
						backgroundColor: colors,
					},
				],
			});
			setAgeData({
				labels: ageLabels,
				datasets: [
					{
						label: "Age",
						data: ageGroupInServiceArea.map(
							(x: any) => clickedPark?.attributes[dataFields[x.value]]
						),
						borderColor: colors,
						backgroundColor: colors,
					},
				],
			});

			setDemoData({
				labels: demoLabels,
				datasets: [
					{
						label: "Demographics",
						data: demographicsInServiceArea.map(
							(x: any) => clickedPark?.attributes[dataFields[x.value]]
						),
						borderColor: colors,
						backgroundColor: colors,
					},
				],
			});
		}
	}, [clickedPark]);

	ChartJS.register(
		CategoryScale,
		LinearScale,
		BarElement,
		Title,
		Tooltip,
		Legend
	);

	const convertAttributeFieldNames = (o: any) => {
		let fields = Object.keys(dataFields)
		let values = Object.values(dataFields)

		// update attribute fields to use SQL server names since hard-coded in python GP service  
		let stats: any = {}
		Object.keys(o).forEach((k) => {
			let key = k
			let i = values.indexOf(k)
			if (i >= 0) {
				key = fields[i]
			}
			stats[key] = o[k]
		})
		return stats
	}

	// This function submits the job and required parameters to the print service
	const submitJob = async () => {
		handleOpen();
		setGPSuccess(false);

		const newReport = parkReport;
		// area table
		newReport.tables.Area.body.fields[0].value = Math.round(
			city?.attributes[dataFields["Total_Park_Acreage"]]
		);
		// population table
		newReport.tables.Population.body.fields[0].value = Math.round(
			clickedPark?.attributes[dataFields["SUM_TOTPOPSVCA"]]
		);
		// age table
		newReport.tables.Age.body.fields.forEach((field, i) => {
			newReport.tables.Age.body.fields[i].value = Math.round(
				clickedPark?.attributes[dataFields[newReport.tables.Age.body.fields[i].pname]]
			);
		});
		// income table
		newReport.tables.Income.body.fields.forEach((field, i) => {
			newReport.tables.Income.body.fields[i].value = Math.round(
				clickedPark?.attributes[dataFields[newReport.tables.Income.body.fields[i].pname]]
			);
		});
		// race table
		newReport.tables.Race.body.fields.forEach((field, i) => {
			newReport.tables.Race.body.fields[i].value = Math.round(
				clickedPark?.attributes[dataFields[newReport.tables.Race.body.fields[i].pname]]
			);
		});

		// other parameters
		newReport.ProjectName = `${city?.attributes[dataFields["Place_StAbbrev"]]} - ${parkData.attributes[dataFields["Park_Name"]]}`;
		newReport.CityOrUrbanID = city?.attributes[dataFields["PLACE_ID"]];

		//newReport.PlaceStats = city?.attributes;
		//newReport.UrbanStats = urban?.attributes;

		// update attribute fields to use SQL server names since hard-coded in python GP service 
		newReport.PlaceStats = convertAttributeFieldNames(city?.attributes);
		newReport.UrbanStats = convertAttributeFieldNames(urban?.attributes);

		const report_json = {
			report: newReport,
		};

		const parkFeatureSet = new FeatureSet({
			displayFieldName: "Name",
			geometryType: "polygon",
			spatialReference: {
				wkid: 102100,
			},
			fields: [
				{
					name: "OBJECTID",
					type: "oid",
					alias: "OBJECTID",
				},
				{
					name: "Name",
					type: "string",
					alias: "Park Name",
					length: 150,
				},
			],
			features: [clickedPark],
			exceededTransferLimit: false,
		});

		const parkServiceAreaFeatureSet = new FeatureSet({
			displayFieldName: "Name",
			geometryType: "polygon",
			spatialReference: {
				wkid: 102100,
			},
			fields: [
				{
					name: "OBJECTID",
					type: "oid",
					alias: "OBJECTID",
				},
				{
					name: "Name",
					type: "string",
					alias: "Service Area Name",
					length: 150,
				},
			],
			features: [
				{
					geometry: clickedParkServiceArea.geometry,
					attributes: {
						OBJECTID: 1,
						Name: clickedParkServiceArea.attributes[dataFields["Park_Name"]],
					},
				},
			],
			exceededTransferLimit: false,
		});

		var gpParams = {
			INPUT_REPORT_JSON: JSON.stringify(report_json),
			INPUT_PARK_FS: parkFeatureSet,
			INPUT_SERVICEAREA_FS: parkServiceAreaFeatureSet,
		};

		const jobInfo: JobInfo = await geoprocessor.submitJob(gpPDF_URL, gpParams);

		// The callback below monitors the progress of the GP Service to be communicated to the user
		const options = {
			interval: 3000,
			statusCallback: (j: any) => {
				if (j.messages.length) {
					setJobMessage(j.messages[j.messages.length - 1].description);
					// console.log("Job Status: ", j.jobStatus);
					// console.log(
					// 	"Job Message: ",
					// 	j.messages[j.messages.length - 1].description
					// );
				} else {
					setJobMessage(
						"Unable to contact server.  Please wait or try again later."
					);
				}
				if (j.jobStatus === "esriJobFailed") {
					console.log("Error: Job failed.");
				}
			},
		};

		jobInfo.waitForJobCompletion(options).then((resp: any) => {
			getJobResultData(jobInfo);

			//console.log(resp);
		});

		// This function is triggered once the job is complete
		const getJobResultData = (jobInfo: JobInfo) => {
			jobInfo.fetchResultData("output_file").then((r: any) => {
				setGPSuccess(true);
				setPdfUrl(r.value.url);
			});
		};
	};

	return (
		<>
			<Typography
				sx={{ textAlign: "center" }}
				variant="h1"
				color="earth.main"
				gutterBottom
			>
				Selected Park
			</Typography>
			<Typography
				variant="h2"
				color="grass.main"
				sx={{ textAlign: "center", marginTop: "5px", marginBottom: "10px" }}
			>
				{parkData.attributes.Park_Name}
			</Typography>
			<Box
				display="flex"
				alignItems="center"
				justifyContent="center"
			>
				<Button
					aria-label="Export Access Statistics PDF"
					onClick={() => {
						trackEvent("export_statistics_click", "park");
						//trackExportPDF("park");
						submitJob();
					}}
					variant="contained"
				>
					Export Access Statistics PDF
				</Button>
			</Box>
			<Box
				display="flex"
				flexDirection="column"
				justifyContent="space-between"
				sx={{ marginTop: "15px", marginBottom: "5px" }}
			>
				<Box
					display="flex"
					flexDirection="row"
					justifyContent="space-between"
					width="100%"
					sx={{ marginTop: "15px", marginBottom: "5px" }}
				>
					<Box
						display="flex"
						flexDirection="column"
						width="110px"
						marginLeft="10px"
					>
						<Typography
							variant="subtitle2"
							color=""
						>
							Park Designation
						</Typography>
						<Typography
							variant="h3"
							color=""
						>
							{parkLabels[parkData?.attributes[dataFields["Park_Designation"]]]}{" "}
						</Typography>
					</Box>
					<Box
						display="flex"
						flexDirection="column"
						width="110px"
					>
						<Typography
							variant="subtitle2"
							color=""
						>
							Park Local Owner
						</Typography>
						<Typography
							variant="h3"
							color=""
						>
							{parkData?.attributes[dataFields["Park_Local_Owner"]]}
						</Typography>
					</Box>
				</Box>
				<Box
					display="flex"
					flexDirection="row"
					justifyContent="space-between"
					width="100%"
					sx={{ marginTop: "15px", marginBottom: "5px" }}
				>
					<Box
						display="flex"
						flexDirection="column"
						width="110px"
						marginLeft="10px"
					>
						<Typography
							variant="subtitle2"
							color=""
						>
							Number of Residents within a 10-Minute Walk
						</Typography>
						<Typography
							variant="h3"
							color=""
						>
							{parkData?.attributes[dataFields["SUM_TOTPOPSVCA"]]?.toLocaleString()}
						</Typography>
					</Box>
					<Box
						display="flex"
						flexDirection="column"
						width="110px"
					>
						<Typography
							variant="subtitle2"
							color=""
						>
							Park Area
						</Typography>
						<Typography
							variant="h3"
							color=""
						>
							{Math.round(parkData?.attributes[dataFields["Park_Size_Acres"]]) + " Acres"}
						</Typography>
					</Box>
				</Box>
			</Box>

			<Divider sx={{ marginTop: "25px" }} />

			{parkData?.attributes[dataFields["SUM_TOTPOPSVCA"]] < 1 ? (
				<Typography
					variant="h5"
					color="#F7931E"
					sx={{ marginBottom: "10px", marginTop: "15px" }}
				>
					This park either does not allow public access or has zero population
					within a 10-minute walk
				</Typography>
			) : (
				<div>
					<Typography
						variant="h5"
						color="grass.main"
						sx={{ marginBottom: "10px" }}
					>
						Race/Ethnicity
					</Typography>
					<Typography
						variant="subtitle2"
						color=""
					>
						Number of residents within a 10-minute walk by race/ethnicity.
						*Excludes those that report Hispanic origin (which is captured
						separately from race by the U.S. Census).
					</Typography>
					<Divider />

					<Box
						height="400px"
						width="100%"
					>
						<Bar
							options={options(demoLabels)}
							plugins={[ChartDataLabels]}
							data={demoData}
						/>
					</Box>

					<Divider sx={{ marginTop: "25px" }} />
					<Typography
						variant="h5"
						color="grass.main"
						sx={{ marginBottom: "10px" }}
					>
						Age
					</Typography>
					<Typography
						variant="subtitle2"
						color=""
					>
						Number of residents within a 10-minute walk by age group
					</Typography>

					<Divider />
					<Box
						height="200px"
						width="100%"
					>
						<Bar
							options={options(ageLabels)}
							plugins={[ChartDataLabels]}
							data={ageData}
						/>
					</Box>

					<Divider sx={{ marginTop: "25px" }} />
					<Typography
						variant="h5"
						color="grass.main"
						sx={{ marginBottom: "10px" }}
					>
						Income
					</Typography>
					<Typography
						variant="subtitle2"
						color=""
					>
						Number of households within a 10-minute walk by income. MAI = Median
						Area Income, which is the median income of the urban area.
					</Typography>
				</div>
			)}

			<Divider />
			<Box
				height="200px"
				width="100%"
				id="park_scroll"
			>
				<Bar
					options={options(incomeLabels)}
					plugins={[ChartDataLabels]}
					data={incomeData}
				/>
			</Box>
			<Divider sx={{ marginTop: "25px" }} />
			<Typography
				variant="h6"
				color="headingGrey.main"
			>
				Demographic information sourced from Esri 2025
			</Typography>

			<Modal
				open={open}
				onClose={handleClose}
				aria-labelledby="modal-modal-title"
				aria-describedby="modal-modal-description"
			>
				<Box sx={modalStyle}>
					<IconButton
						aria-label="close"
						onClick={handleClose}
						sx={{
							position: "absolute",
							right: 8,
							top: 8,
							color: (theme) => theme.palette.grey[500],
						}}
					>
						<CloseIcon />
					</IconButton>

					{!gpSuccess && (
						<>
							<Typography
								id="modal-modal-title"
								variant="h3"
								component="h2"
							>
								Generating PDF report...
							</Typography>
							<Typography
								id="modal-modal-description"
								sx={{ mt: 2 }}
							>
								{jobMessage}
							</Typography>
						</>
					)}
					{gpSuccess && (
						<>
							<Typography
								id="modal-modal-title"
								variant="h3"
								component="h2"
							>
								Success!
							</Typography>
							<Button
								variant="contained"
								sx={{ marginTop: "10px" }}
								href={pdfUrl}
								target="_blank"
								onClick={() => {
									trackEvent("export_statistics_pdf", "park");
								}}
							>
								Download PDF Report
							</Button>
						</>
					)}
				</Box>
			</Modal>
		</>
	);
}
